import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function UniversitiesCampuses() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Universities & Campuses'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2843">
      <Header data-id="element-2844" />
      <main className="pt-[120px]" data-id="element-2845">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-2846">
          <div className="max-w-3xl mx-auto" data-id="element-2847">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2848">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-2849">
                {isFR ? 'SECTEURS · UNIVERSITÉS & CAMPUS' : 'INDUSTRIES · UNIVERSITIES & CAMPUSES'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2850">
                {isFR ? 'Un campus est une mini-ville.' : 'A campus is a mini-city.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-2851">
                {isFR ? "L'IA arrive vite sur les campus — plus de la moitié des établissements l'utilisent déjà. Mais elle cale là où les données étudiantes vivent dans cinquante systèmes déconnectés. La fondation d'abord, l'intelligence ensuite." : 'AI is arriving on campus fast — over half of institutions already use it. But it stalls where student data lives in fifty disconnected systems. Foundation first, intelligence second.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-2852">
                <Link to="/request-demo?industry=universities" onClick={() => track('Demo CTA Clicked', {
                location: 'Universities Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2853">
                  {isFR ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2854" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2855">
          <div className="max-w-3xl mx-auto" data-id="element-2856">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-2857">
              {isFR ? 'La recherche montre' : 'Research Shows'}
            </motion.div>
            <motion.blockquote {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="border-l-2 border-[#4F46E5] pl-6 mb-8" data-id="element-2858">
              <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug" data-id="element-2859">
                {isFR ? "Le BCG constate que seulement 5 % des organisations captent la valeur qu'elles attendent de l'IA — et que 10 % seulement du succès provient des modèles eux-mêmes. Les 90 % restants, c'est la fondation : les personnes, les processus et des données de confiance. Les universités fonctionnent sur des dizaines de systèmes déconnectés. C'est la fondation qui manque." : 'BCG finds that only 5% of organizations capture the value they expect from AI — and that just 10% of success comes from the models themselves. The other 90% is foundation: people, processes and trusted data. Universities run on dozens of disconnected systems. The foundation is what’s missing.'}
              </p>
            </motion.blockquote>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-2860">
              {isFR ? 'Contexte de marché. Pas des résultats de déploiement BCITI. Source : ' : 'Market context. Not BCITI deployment results. Source: '}
              <a href="https://www.bcg.com/publications/2026/how-ai-can-help-universities-capture-opportunity" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-2861">
                BCG, &ldquo;How AI Can Help Higher Education Capture a Once-in-a-Generation Opportunity,&rdquo; {isFR ? 'mars 2026.' : 'March 2026.'}
              </a>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-2862">
          <div className="max-w-3xl mx-auto" data-id="element-2863">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-2864">
              {isFR ? "La carte étudiante, c'est notre Carte BCITI : identité, accès, paiements et services en un. La même fondation qu'une ville — prête pour l'intelligence quand le campus l'est." : 'The student card is our BCITI Card: identity, access, payments and services in one. The same foundation as a city — ready for intelligence when the campus is.'}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-2865" />
    </div>;
}